import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  BookOpen, 
  CheckCircle2,
  Clock,
  Play,
  Award,
  TrendingUp
} from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/stats/employee`, { withCredentials: true }),
        axios.get(`${API}/courses`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'overdue': return 'danger';
      default: return 'neutral';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const assignments = stats?.assignments || [];
  const assignedCourses = assignments.map(a => {
    const course = courses.find(c => c.course_id === a.course_id);
    return { ...a, course };
  });

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8" data-testid="employee-dashboard">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
            My Training Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Track your safety training progress and certifications.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Assigned Courses</p>
                  <p className="font-heading text-2xl font-bold text-primary">{stats?.total || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Completed</p>
                  <p className="font-heading text-2xl font-bold text-primary">{stats?.completed || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="stat-card">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Completion Rate</p>
                  <p className="font-heading text-2xl font-bold text-primary">{stats?.completion_rate || 0}%</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Overview */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading font-semibold text-primary">Your Progress</h3>
                <p className="text-sm text-muted-foreground">Overall training completion</p>
              </div>
              <div className="text-right">
                <span className="font-heading text-3xl font-bold text-primary">{stats?.completion_rate || 0}%</span>
                <p className="text-sm text-muted-foreground">{stats?.completed || 0} of {stats?.total || 0} courses</p>
              </div>
            </div>
            <Progress value={stats?.completion_rate || 0} className="h-4" />
          </CardContent>
        </Card>

        {/* My Courses */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-semibold text-primary">My Courses</h2>
            <Link to="/courses">
              <Button variant="outline" data-testid="browse-courses-btn">
                Browse All Courses
              </Button>
            </Link>
          </div>

          {assignedCourses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {assignedCourses.map((item) => (
                <Card key={item.assignment_id} className="course-card" data-testid={`course-card-${item.assignment_id}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <Badge className={`badge-${getStatusColor(item.status)}`}>
                        {item.status.replace('_', ' ')}
                      </Badge>
                      {item.score !== null && (
                        <div className="flex items-center gap-1 text-amber-600">
                          <Award className="w-4 h-4" />
                          <span className="font-semibold">{item.score}%</span>
                        </div>
                      )}
                    </div>
                    
                    <h3 className="font-heading font-semibold text-primary mb-2">
                      {item.course?.title || 'Course'}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {item.course?.description || 'No description'}
                    </p>

                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium text-primary">{item.progress}%</span>
                      </div>
                      <Progress value={item.progress} className="h-2" />
                    </div>

                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{item.course?.duration_minutes || 30} min</span>
                      </div>
                      <Badge variant="secondary">{item.course?.category || 'General'}</Badge>
                    </div>

                    <Link to={`/course/${item.course_id}`}>
                      <Button 
                        className="w-full" 
                        variant={item.status === 'completed' ? 'outline' : 'default'}
                        data-testid={`start-course-${item.assignment_id}`}
                      >
                        {item.status === 'completed' ? (
                          <>Review Course</>
                        ) : item.status === 'in_progress' ? (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            Continue
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            Start Course
                          </>
                        )}
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <BookOpen className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-heading text-lg font-semibold text-primary mb-2">No Courses Assigned</h3>
                <p className="text-muted-foreground mb-4">
                  You don't have any training courses assigned yet. Browse available courses below.
                </p>
                <Link to="/courses">
                  <Button data-testid="browse-all-courses-btn">
                    Browse Available Courses
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmployeeDashboard;
